interface NavigationElements {
  mainNavDesktop: HTMLElement | null;
  mainNavMobile: HTMLElement | null;
  dropdownToggles: NodeListOf<HTMLElement>;
  mobileMenu: HTMLElement | null;
  mobileNavToggle: HTMLElement | null;
}

const CONFIG = {
  MOBILE_BREAKPOINT: 1024,
  SCROLL_THRESHOLD: 5,
  HEADER_HEIGHT: 100,
  MENU_TOGGLE_DELAY: 0,
  THROTTLE_DELAY: 16,        // ~60fps
  SIDEMENU_SCROLL_TIMEOUT: 1000,
} as const;

const SELECTORS = {
  MAIN_NAV_CONTAINER_DESKTOP: '.desktop',
  MAIN_NAV_CONTAINER_MOBILE: '.mobile',
  DROPDOWN_TOGGLE: '.dropdown-toggle',
  DROPDOWN_MENU: '.dropdown-menu',
  MOBILE_MENU: '.mobile-menu',
  MOBILE_NAVIGATION_TOGGLE: '.mobile-navigation-toggle',
} as const;

const CSS_CLASSES = {
  FIXED_MENU_DOWN: 'fixed-menu-down',
  HIDDEN: 'hidden',
  SUBNAV: 'subnav',
} as const;

/** Defers repeated calls to the next animation frame. */
function throttleRAF<TFunc extends (...args: any[]) => any>(
  callback: TFunc
): (...args: Parameters<TFunc>) => void {
  let rafId: number | null = null;
  let lastArgs: Parameters<TFunc>;

  return function (...args: Parameters<TFunc>) {
    lastArgs = args;
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        callback(...lastArgs);
        rafId = null;
      });
    }
  };
}

/** Limits how often a callback fires, guaranteeing a trailing call. */
function throttle<TFunc extends (...args: any[]) => any>(
  callback: TFunc,
  delay: number
): (...args: Parameters<TFunc>) => void {
  let timeoutId: number | null = null;
  let lastExecTime = 0;

  return function (...args: Parameters<TFunc>) {
    const now = Date.now();
    const timeSinceLastExec = now - lastExecTime;

    if (timeSinceLastExec >= delay) {
      lastExecTime = now;
      callback(...args);
    } else if (timeoutId === null) {
      timeoutId = window.setTimeout(() => {
        lastExecTime = Date.now();
        timeoutId = null;
        callback(...args);
      }, delay - timeSinceLastExec);
    }
  };
}

/**
 * Handles header navigation: sticky show/hide on scroll, desktop dropdowns,
 * mobile menu with focus trap, and sidemenu-scroll suppression.
 */
class Navigation {
  private elements: NavigationElements;
  private state: {
    isMenuToggling: boolean;
    lastScrollTop: number;
    toggleTimeout: number | null;
    isSidemenuScrolling: boolean;
    sidemenuScrollTimeout: number | null;
  };

  private boundEventHandlers: {
    scroll: () => void;
    resize: () => void;
    outsideClick: (event: Event) => void;
    mobileNavToggle: () => void;
    dropdownToggles: Map<HTMLElement, (event: Event) => void>;
  };

  private focusTrapHandler: ((event: KeyboardEvent) => void) | null = null;

  constructor() {
    this.state = {
      isMenuToggling: false,
      lastScrollTop: 0,
      toggleTimeout: null,
      isSidemenuScrolling: false,
      sidemenuScrollTimeout: null,
    };

    this.boundEventHandlers = {
      scroll: throttleRAF(this.onScroll.bind(this)),
      resize: throttle(this.onResize.bind(this), CONFIG.THROTTLE_DELAY),
      outsideClick: this.handleOutsideClick.bind(this),
      mobileNavToggle: this.onMobileNavToggleClick.bind(this),
      dropdownToggles: new Map(),
    };

    this.elements = this.initializeElements();

    if (!this.elements.mainNavDesktop && !this.elements.mainNavMobile) {
      console.error('Navigation: No navigation containers found');
      return;
    }

    this.addEventListeners();
  }

  private initializeElements(): NavigationElements {
    return {
      // Desktop nav wraps a .desktop child — take the parent element
      mainNavDesktop: document.querySelector<HTMLElement>(SELECTORS.MAIN_NAV_CONTAINER_DESKTOP)?.parentElement ?? null,
      mainNavMobile: document.querySelector<HTMLElement>(SELECTORS.MAIN_NAV_CONTAINER_MOBILE),
      dropdownToggles: document.querySelectorAll<HTMLElement>(SELECTORS.DROPDOWN_TOGGLE),
      mobileMenu: document.querySelector<HTMLElement>(SELECTORS.MOBILE_MENU),
      mobileNavToggle: document.querySelector<HTMLElement>(SELECTORS.MOBILE_NAVIGATION_TOGGLE),
    };
  }

  private addEventListeners(): void {
    window.addEventListener('scroll', this.boundEventHandlers.scroll, { passive: true });
    window.addEventListener('resize', this.boundEventHandlers.resize, { passive: true });
    document.addEventListener('click', this.boundEventHandlers.outsideClick);

    this.elements.dropdownToggles.forEach(toggle => {
      const handler = (e: Event) => this.onDropdownToggleClick(e, toggle);
      this.boundEventHandlers.dropdownToggles.set(toggle, handler);
      toggle.addEventListener('click', handler);
    });

    if (this.elements.mobileNavToggle) {
      this.elements.mobileNavToggle.addEventListener('click', this.boundEventHandlers.mobileNavToggle);
    }
  }

  private removeEventListeners(): void {
    window.removeEventListener('scroll', this.boundEventHandlers.scroll);
    window.removeEventListener('resize', this.boundEventHandlers.resize);
    document.removeEventListener('click', this.boundEventHandlers.outsideClick);

    this.boundEventHandlers.dropdownToggles.forEach((handler, toggle) => {
      toggle.removeEventListener('click', handler);
    });
    this.boundEventHandlers.dropdownToggles.clear();

    if (this.elements.mobileNavToggle) {
      this.elements.mobileNavToggle.removeEventListener('click', this.boundEventHandlers.mobileNavToggle);
    }
  }

  /**
   * Handles document-level clicks: closes dropdowns when clicking outside them,
   * and detects sidemenu anchor navigation to suppress the header from showing
   * during the programmatic scroll that follows.
   */
  private handleOutsideClick(event: Event): void {
    const target = event.target as Element;

    // Detect sidemenu anchor clicks so onScroll can suppress header show
    const sidemenuLink = target.closest('a[href^="#section-"], .side-menu-item a[href^="#"]');
    if (sidemenuLink) {
      this.state.isSidemenuScrolling = true;
      if (this.state.sidemenuScrollTimeout !== null) {
        clearTimeout(this.state.sidemenuScrollTimeout);
      }
      this.state.sidemenuScrollTimeout = window.setTimeout(() => {
        this.state.isSidemenuScrolling = false;
        this.state.sidemenuScrollTimeout = null;
      }, CONFIG.SIDEMENU_SCROLL_TIMEOUT);
    }

    // Close all dropdowns when clicking outside both the toggle and the menu
    if (!target.closest(SELECTORS.DROPDOWN_TOGGLE) && !target.closest(SELECTORS.DROPDOWN_MENU)) {
      this.hideAllDropdowns();
    }
  }

  private onScroll(): void {
    if (this.state.isMenuToggling || !this.elements.mainNavDesktop) return;

    const scrollTop = window.scrollY;
    const scrollDelta = scrollTop - this.state.lastScrollTop;

    const isScrollingDown = scrollDelta > CONFIG.SCROLL_THRESHOLD && scrollTop > 400;
    const isScrollingUp = scrollDelta < -CONFIG.SCROLL_THRESHOLD;

    if (!isScrollingDown && !isScrollingUp) return;

    this.updateNavigationVisibility(scrollTop, isScrollingUp, isScrollingDown);
    this.state.lastScrollTop = scrollTop;
  }

  private updateNavigationVisibility(scrollTop: number, isScrollingUp: boolean, isScrollingDown: boolean): void {
    if (!this.elements.mainNavDesktop) return;

    const classList = this.elements.mainNavDesktop.classList;

    if (isScrollingUp && scrollTop > CONFIG.HEADER_HEIGHT && !this.state.isSidemenuScrolling) {
      classList.add(CSS_CLASSES.FIXED_MENU_DOWN);
    } else if (isScrollingDown) {
      classList.remove(CSS_CLASSES.FIXED_MENU_DOWN);
    }
  }

  private hideAllDropdowns(): void {
    document.querySelectorAll<HTMLElement>(SELECTORS.DROPDOWN_MENU).forEach(menu => {
      menu.classList.add(CSS_CLASSES.HIDDEN);
    });
    this.elements.dropdownToggles.forEach(toggle => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  private toggleDropdown(toggle: HTMLElement, dropdownMenu: HTMLElement): void {
    this.setMenuToggling(true);
    const isHidden = dropdownMenu.classList.contains(CSS_CLASSES.HIDDEN);
    if (isHidden) {
      this.hideAllDropdowns();
      this.showDropdown(toggle, dropdownMenu);
    } else {
      this.hideDropdown(toggle, dropdownMenu);
    }
    this.setMenuToggling(false, CONFIG.MENU_TOGGLE_DELAY);
  }

  private showDropdown(toggle: HTMLElement, dropdownMenu: HTMLElement): void {
    toggle.setAttribute('aria-expanded', 'true');
    dropdownMenu.classList.remove(CSS_CLASSES.HIDDEN);
    dropdownMenu.setAttribute('aria-hidden', 'false');
  }

  private hideDropdown(toggle: HTMLElement, dropdownMenu: HTMLElement): void {
    toggle.setAttribute('aria-expanded', 'false');
    dropdownMenu.classList.add(CSS_CLASSES.HIDDEN);
    dropdownMenu.setAttribute('aria-hidden', 'true');
  }

  private setMenuToggling(isToggling: boolean, delay = 0): void {
    if (this.state.toggleTimeout !== null) {
      clearTimeout(this.state.toggleTimeout);
      this.state.toggleTimeout = null;
    }

    if (delay > 0) {
      this.state.toggleTimeout = window.setTimeout(() => {
        this.state.isMenuToggling = isToggling;
        this.state.toggleTimeout = null;
      }, delay);
    } else {
      this.state.isMenuToggling = isToggling;
    }
  }

  private onDropdownToggleClick(event: Event, toggle: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();

    const dropdownMenu = toggle.nextElementSibling as HTMLElement | null;
    if (!dropdownMenu) {
      console.warn('Navigation: Dropdown menu not found for toggle', toggle);
      return;
    }

    if (window.innerWidth < CONFIG.MOBILE_BREAKPOINT) {
      this.handleMobileDropdownClick(toggle, dropdownMenu);
    } else {
      this.toggleDropdown(toggle, dropdownMenu);
    }
  }

  private handleMobileDropdownClick(toggle: HTMLElement, dropdownMenu: HTMLElement): void {
    if (toggle.classList.contains(CSS_CLASSES.SUBNAV)) {
      this.toggleDropdown(toggle, dropdownMenu);
      return;
    }

    if (this.elements.mobileMenu) {
      const wasHidden = this.elements.mobileMenu.classList.contains(CSS_CLASSES.HIDDEN);
      this.setMenuToggling(true);
      if (wasHidden) {
        this.showMobileMenu();
      } else {
        this.hideMobileMenu();
      }
      this.updateMobileMenuAria();
      this.setMenuToggling(false, CONFIG.MENU_TOGGLE_DELAY);
    }
  }

  private onResize(): void {
    if (window.innerWidth >= CONFIG.MOBILE_BREAKPOINT && this.elements.mobileMenu) {
      this.elements.mobileMenu.classList.add(CSS_CLASSES.HIDDEN);
    }
    this.hideAllDropdowns();
  }

  private showMobileMenu(): void {
    if (!this.elements.mobileMenu) return;
    this.elements.mobileMenu.classList.remove(CSS_CLASSES.HIDDEN);
    this.trapFocusInMobileMenu();
  }

  private hideMobileMenu(): void {
    if (!this.elements.mobileMenu) return;
    this.elements.mobileMenu.classList.add(CSS_CLASSES.HIDDEN);
    this.releaseFocusTrap();
  }

  private trapFocusInMobileMenu(): void {
    if (!this.elements.mobileMenu) return;

    const focusableSelectors = [
      'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
      'input[type="text"]:not([disabled])', 'input[type="radio"]:not([disabled])',
      'input[type="checkbox"]:not([disabled])', 'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ];

    const focusableEls = Array.from(
      this.elements.mobileMenu.querySelectorAll<HTMLElement>(focusableSelectors.join(','))
    ).filter(el => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');

    if (!focusableEls.length) return;

    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    this.focusTrapHandler = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        if (event.shiftKey && document.activeElement === firstEl) {
          event.preventDefault();
          lastEl.focus();
        } else if (!event.shiftKey && document.activeElement === lastEl) {
          event.preventDefault();
          firstEl.focus();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.hideMobileMenu();
        this.updateMobileMenuAria();
        this.elements.mobileNavToggle?.focus();
      }
    };

    document.addEventListener('keydown', this.focusTrapHandler);
    firstEl.focus();
  }

  private releaseFocusTrap(): void {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  private onMobileNavToggleClick(): void {
    if (this.state.isMenuToggling || !this.elements.mobileMenu) return;

    this.setMenuToggling(true);

    const wasHidden = this.elements.mobileMenu.classList.contains(CSS_CLASSES.HIDDEN);
    if (wasHidden) {
      this.showMobileMenu();
    } else {
      this.hideMobileMenu();
    }

    this.updateMobileMenuAria();
    this.setMenuToggling(false, CONFIG.MENU_TOGGLE_DELAY);
  }

  private updateMobileMenuAria(): void {
    const isHidden = this.elements.mobileMenu?.classList.contains(CSS_CLASSES.HIDDEN);
    this.elements.mobileNavToggle?.setAttribute('aria-expanded', isHidden ? 'false' : 'true');
    this.elements.mobileMenu?.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
  }

  /** Removes all event listeners and clears timers. */
  public destroy(): void {
    if (this.state.toggleTimeout !== null) {
      clearTimeout(this.state.toggleTimeout);
      this.state.toggleTimeout = null;
    }
    if (this.state.sidemenuScrollTimeout !== null) {
      clearTimeout(this.state.sidemenuScrollTimeout);
      this.state.sidemenuScrollTimeout = null;
    }

    this.removeEventListeners();
    this.releaseFocusTrap();
    this.elements = {} as NavigationElements;
  }

  /** Tears down and re-initializes when the DOM has changed. */
  public reinitialize(): void {
    this.destroy();
    this.elements = this.initializeElements();

    if (this.elements.mainNavDesktop || this.elements.mainNavMobile) {
      this.addEventListeners();
    }
  }
}

let navigationInstance: Navigation | null = null;

function initializeNavigation(): void {
  if (navigationInstance) {
    navigationInstance.destroy();
  }
  navigationInstance = new Navigation();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeNavigation);
} else {
  initializeNavigation();
}

window.addEventListener('beforeunload', () => {
  navigationInstance?.destroy();
});

export default Navigation;
